/**
 * Call shifts of the Clients board. Leads say in the form when they want to be
 * called; setters work one shift at a time (morning, midday, evening).
 */

export type ShiftKey = "all" | "9-14" | "14-18" | "18-21" | "none";

export interface Shift {
  key: ShiftKey;
  label: string;
  hours?: string;
}

export const SHIFTS: Shift[] = [
  { key: "all", label: "Todos" },
  { key: "9-14", label: "Mañana", hours: "9 a 14 h" },
  { key: "14-18", label: "Mediodía", hours: "14 a 18 h" },
  { key: "18-21", label: "Tarde", hours: "18 a 21 h" },
  { key: "none", label: "Sin franja" },
];

/**
 * Stages where leads are called: the shift tabs only filter these columns.
 * "Compra segura" stays visible in every shift: those are the sales to close.
 */
export const CALLING_STAGES = [
  "new",
  "no_answer",
  "first_contact",
  "second_contact",
  "recontact",
];

const isShiftKey = (value: string | null): value is ShiftKey =>
  SHIFTS.some((shift) => shift.key === value);

/**
 * Shift to show: the one in the URL (`turno`, or the older `franja` links from
 * Inicio, where "now" means the running slot), else the running slot, else all.
 */
export const resolveShift = (
  turno: string | null,
  franja: string | null,
  currentSlot: string | null,
): ShiftKey => {
  if (isShiftKey(turno)) return turno;
  if (franja === "now") return (currentSlot as ShiftKey | null) ?? "all";
  if (isShiftKey(franja)) return franja;
  return (currentSlot as ShiftKey | null) ?? "all";
};

const SLOT_KEYS = ["9-14", "14-18", "18-21"];

/** Standard slots only: anything else counts as "Sin franja". */
export const slotsOf = (callSlot?: string | null) =>
  (callSlot ?? "")
    .split(",")
    .map((slot) => slot.trim())
    .filter((slot) => SLOT_KEYS.includes(slot));

/** A lead that chose several slots shows up in each of them. */
export const inShift = (
  lead: { stage: string; call_slot?: string | null },
  shift: ShiftKey,
): boolean => {
  if (shift === "all" || !CALLING_STAGES.includes(lead.stage)) return true;
  const slots = slotsOf(lead.call_slot);
  return shift === "none" ? slots.length === 0 : slots.includes(shift);
};

/** Leads to call per shift tab. */
export const countByShift = (
  leads: { stage: string; call_slot?: string | null }[],
): Record<ShiftKey, number> => {
  const calling = leads.filter((lead) => CALLING_STAGES.includes(lead.stage));
  return Object.fromEntries(
    SHIFTS.map((shift) => [
      shift.key,
      calling.filter((lead) => inShift(lead, shift.key)).length,
    ]),
  ) as Record<ShiftKey, number>;
};
